import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import {
  DEFAULT_STATE,
  type StatisticType,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { type ActionsFactory, type StoreInstance, createStore, createStoreInstance } from '@seeqdev/utils';

import type { InterpolationMethod } from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.constants';
import type { ControlChartSelectedOutputs } from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.types';
import type { BaseToolPersistedState } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export interface OperationalizeStatisticsSummaryState extends BaseToolPersistedState {
  /** User-facing name of the operationalized item. */
  name: string;
  /** ID of the statistics summary baseline the operationalization is derived from, or `null` if none. */
  baselineId: string | null;
  /** ID of the signal being operationalized, or `null` if none is selected. */
  inputSignalId: string | null;
  /** ID of the condition that constrains which samples should be used in calculations, or `null` if none. */
  conditionId: string | null;
  /** ID of the condition used to align sample groups, or `null` when alignment is manual/formula-based. */
  alignmentConditionId: string | null;
  /** Manually specified alignment condition formula, used when no `alignmentConditionId` is set. */
  alignmentConditionFormula?: string;
  /** Maximum capsule duration override for the alignment condition. */
  alignmentMaxDuration?: ValueWithUnitsItem;
  /** `true` when the user explicitly chose the alignment condition. */
  hasManualAlignmentCondition: boolean;
  /** Per-chart map of which control-chart output groups the user has selected to produce. */
  controlChartSelectedOutputs: ControlChartSelectedOutputs;
  /** ID of the periodic condition that defines the summarization windows for statistics, or `null` if none. */
  periodicConditionId: string | null;
  /** Statistics the user has chosen to produce (mean, range, etc.). */
  selectedStatistics: StatisticType[];
  /** Where each output sample's timestamp is placed within its window: start, end, or duration. */
  timestampPlacement: string;
  /** Interpolation method applied to the produced statistic signals. */
  interpolationMethod: InterpolationMethod;
  /** Maximum capsule duration for the periodic condition. */
  maxCapsuleDuration: ValueWithUnitsItem;
  /** Maximum interpolation override for the produced statistic signals. */
  maxInterpolation?: ValueWithUnitsItem;
}

interface OperationalizeStatisticsSummaryActions {
  setName: (name: string) => void;
  setInputSignalId: (signalId: string | null) => void;
  setConditionId: (conditionId: string | null) => void;
  setAlignmentConditionId: (conditionId: string | null) => void;
  setAlignmentConditionFormula: (formula?: string) => void;
  setBaselineId: (baselineId: string | null) => void;
  setAlignmentMaxDuration: (duration?: ValueWithUnitsItem) => void;
  setHasManualAlignmentCondition: (hasManual: boolean) => void;
  setControlChartSelectedOutputs: (controlChartSelectedOutputs: ControlChartSelectedOutputs) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setPeriodicConditionId: (conditionId: string | null) => void;
  setSelectedStatistics: (statistics: StatisticType[]) => void;
  setInterpolationMethod: (method: InterpolationMethod) => void;
  setTimestampPlacement: (placement: string) => void;
  setMaxCapsuleDuration: (duration: ValueWithUnitsItem) => void;
  setMaxInterpolation: (maxInterpolation?: ValueWithUnitsItem) => void;
  rehydrateForEdit: (extractedState: Partial<OperationalizeStatisticsSummaryState>) => void;
  reset: () => void;
}

const initialState: OperationalizeStatisticsSummaryState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  name: '',
  baselineId: null,
  controlChartSelectedOutputs: {},
  inputSignalId: null,
  conditionId: null,
  alignmentConditionId: null,
  alignmentConditionFormula: undefined,
  periodicConditionId: null,
  alignmentMaxDuration: undefined,
  hasManualAlignmentCondition: false,
  selectedStatistics: [...DEFAULT_STATE.selectedStatistics],
  timestampPlacement: DEFAULT_STATE.timestampPlacement,
  interpolationMethod: DEFAULT_STATE.interpolationMethod,
  maxCapsuleDuration: DEFAULT_STATE.maxCapsuleDuration,
  maxInterpolation: undefined,
});

export type OperationalizeStatisticsSummaryDehydratedState = Pick<
  OperationalizeStatisticsSummaryState,
  | 'id'
  | 'name'
  | 'controlChartSelectedOutputs'
  | 'conditionId'
  | 'alignmentConditionId'
  | 'alignmentConditionFormula'
  | 'inputSignalId'
  | 'baselineId'
  | 'periodicConditionId'
  | 'alignmentMaxDuration'
  | 'hasManualAlignmentCondition'
  | 'advancedParametersCollapsed'
  | 'selectedStatistics'
  | 'timestampPlacement'
  | 'interpolationMethod'
  | 'maxCapsuleDuration'
  | 'maxInterpolation'
>;

const operationalizeStatisticsSummaryActionsFactory: ActionsFactory<
  OperationalizeStatisticsSummaryState,
  OperationalizeStatisticsSummaryActions
> = (set, _get, resetState) => ({
  setName: (name: string) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.name = name;
    }),

  setInputSignalId: (signalId: string | null) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.inputSignalId = signalId;
    }),

  setConditionId: (conditionId: string | null) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.conditionId = conditionId;
    }),

  setAlignmentConditionId: (conditionId: string | null) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.alignmentConditionId = conditionId;
    }),

  setAlignmentConditionFormula: (formula?: string) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.alignmentConditionFormula = formula;
    }),

  setBaselineId: (baselineId: string | null) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.baselineId = baselineId;
    }),

  setAlignmentMaxDuration: (duration?: ValueWithUnitsItem) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.alignmentMaxDuration = duration;
    }),

  setHasManualAlignmentCondition: (hasManual: boolean) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.hasManualAlignmentCondition = hasManual;
    }),

  setControlChartSelectedOutputs: (controlChartSelectedOutputs: ControlChartSelectedOutputs) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.controlChartSelectedOutputs = controlChartSelectedOutputs;
    }),

  setPeriodicConditionId: (conditionId: string | null) =>
    set((draft) => {
      draft.periodicConditionId = conditionId;
    }),

  setSelectedStatistics: (statistics: StatisticType[]) =>
    set((draft) => {
      draft.selectedStatistics = statistics;
    }),

  setTimestampPlacement: (placement: string) =>
    set((draft) => {
      draft.timestampPlacement = placement;
    }),

  setInterpolationMethod: (method: InterpolationMethod) =>
    set((draft) => {
      draft.interpolationMethod = method;
    }),

  setMaxCapsuleDuration: (duration: ValueWithUnitsItem) =>
    set((draft) => {
      draft.maxCapsuleDuration = duration;
    }),

  setMaxInterpolation: (maxInterpolation?: ValueWithUnitsItem) =>
    set((draft) => {
      draft.maxInterpolation = maxInterpolation;
    }),

  setAdvancedParametersCollapsed: (collapsed: boolean) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  rehydrateForEdit: (extractedState: Partial<OperationalizeStatisticsSummaryState>) => {
    set((draft) => {
      Object.assign(draft, initialState, extractedState);
    });
  },

  reset: resetState,
});

export const dehydrateOperationalizeStatisticsSummaryState = (
  state: OperationalizeStatisticsSummaryState,
): OperationalizeStatisticsSummaryDehydratedState => ({
  id: state.id,
  name: state.name,
  controlChartSelectedOutputs: state.controlChartSelectedOutputs,
  conditionId: state.conditionId,
  alignmentConditionId: state.alignmentConditionId,
  alignmentConditionFormula: state.alignmentConditionFormula,
  inputSignalId: state.inputSignalId,
  baselineId: state.baselineId,
  periodicConditionId: state.periodicConditionId,
  alignmentMaxDuration: state.alignmentMaxDuration,
  hasManualAlignmentCondition: state.hasManualAlignmentCondition,
  advancedParametersCollapsed: state.advancedParametersCollapsed,
  selectedStatistics: state.selectedStatistics,
  timestampPlacement: state.timestampPlacement,
  interpolationMethod: state.interpolationMethod,
  maxCapsuleDuration: state.maxCapsuleDuration,
  maxInterpolation: state.maxInterpolation,
});

const STORE_NAME = 'OperationalizeStatisticsSummary';

const [
  OperationalizeStatsSummaryStoreProvider,
  useOperationalizeStatsSummaryStoreInstance,
  useOperationalizeStatsSummarySelector,
] = createStore(STORE_NAME, initialState, operationalizeStatisticsSummaryActionsFactory);

export { OperationalizeStatsSummaryStoreProvider };

export const useOperationalizeStatsSummaryActions = () => useOperationalizeStatsSummaryStoreInstance().actions;
export const useOperationalizeStatsSummaryId = () => useOperationalizeStatsSummarySelector((state) => state.id);
export const useOperationalizeStatsSummaryName = () => useOperationalizeStatsSummarySelector((state) => state.name);
export const useOperationalizeStatsSummaryBaselineId = () =>
  useOperationalizeStatsSummarySelector((state) => state.baselineId);
export const useOperationalizeStatsSummaryInputSignalId = () =>
  useOperationalizeStatsSummarySelector((state) => state.inputSignalId);
export const useOperationalizeStatsSummaryConditionId = () =>
  useOperationalizeStatsSummarySelector((state) => state.conditionId);
export const useOperationalizeStatsSummaryAlignmentConditionId = () =>
  useOperationalizeStatsSummarySelector((state) => state.alignmentConditionId);
export const useOperationalizeStatsSummaryAlignmentConditionFormula = () =>
  useOperationalizeStatsSummarySelector((state) => state.alignmentConditionFormula);
export const useOperationalizeStatsSummaryAlignmentMaxDuration = () =>
  useOperationalizeStatsSummarySelector((state) => state.alignmentMaxDuration);
export const useOperationalizeStatsSummaryHasManualAlignmentCondition = () =>
  useOperationalizeStatsSummarySelector((state) => state.hasManualAlignmentCondition);
export const useOperationalizeStatsSummaryPeriodicConditionId = () =>
  useOperationalizeStatsSummarySelector((state) => state.periodicConditionId);
export const useOperationalizeStatsSummaryAdvancedParametersCollapsed = () =>
  useOperationalizeStatsSummarySelector((state) => state.advancedParametersCollapsed);
export const useOperationalizeStatsSummarySelectedStatistics = () =>
  useOperationalizeStatsSummarySelector((state) => state.selectedStatistics);
export const useOperationalizeStatsSummaryTimestampPlacement = () =>
  useOperationalizeStatsSummarySelector((state) => state.timestampPlacement);
export const useOperationalizeStatsSummaryInterpolationMethod = () =>
  useOperationalizeStatsSummarySelector((state) => state.interpolationMethod);
export const useOperationalizeStatsSummaryMaxCapsuleDuration = () =>
  useOperationalizeStatsSummarySelector((state) => state.maxCapsuleDuration);
export const useOperationalizeStatsSummaryMaxInterpolation = () =>
  useOperationalizeStatsSummarySelector((state) => state.maxInterpolation);
export const useOperationalizeStatsSummaryControlChartSelectedOutputs = () =>
  useOperationalizeStatsSummarySelector((state) => state.controlChartSelectedOutputs);

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateOperationalizeStatisticsSummaryStore(): StoreInstance<
  OperationalizeStatisticsSummaryState,
  OperationalizeStatisticsSummaryActions
> {
  return createStoreInstance(initialState, operationalizeStatisticsSummaryActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistOperationalizeStatsSummaryStore = () => {
  const register = useRegisterPersistedStore();
  const store = useOperationalizeStatsSummaryStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqOperationalizeStatisticsSummaryStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateOperationalizeStatisticsSummaryState,
    });
  }, [register, store]);
};
